import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, Check, FileType2, RefreshCw, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui-kit/button';
import type { GenerationStep } from '../hooks/useDocxGeneration';

interface DocxLoadingScreenProps {
  steps: GenerationStep[];
  stepIndex: number;
  /** 0–100, reflecting the real pipeline stage. */
  progress: number;
  error: string | null;
  onRetry: () => void;
  onBack: () => void;
}

/**
 * Full-screen generation status for the fixed-DOCX flow. Mirrors the smart-flow
 * LoadingScreen's look, but the checklist + progress track the REAL pipeline
 * (merge → convert → detect → save), not a simulated timer.
 */
export default function DocxLoadingScreen({
  steps,
  stepIndex,
  progress,
  error,
  onRetry,
  onBack,
}: DocxLoadingScreenProps) {
  const currentStep = steps[Math.min(stepIndex, steps.length - 1)];

  if (error) {
    return (
      <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[hsl(var(--bg))] px-3 sm:px-4 py-6 sm:py-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md rounded-2xl sm:rounded-3xl border border-red-200 bg-background p-5 sm:p-8 shadow-xl text-center"
        >
          <div className="mx-auto mb-4 sm:mb-6 flex h-14 w-14 sm:h-20 sm:w-20 items-center justify-center rounded-2xl sm:rounded-3xl bg-red-50">
            <AlertTriangle className="h-7 w-7 sm:h-10 sm:w-10 text-red-500" />
          </div>
          <h2 className="text-lg sm:text-2xl font-semibold text-[hsl(var(--fg))] mb-2 sm:mb-3 font-heading">
            We couldn&apos;t generate your document
          </h2>
          <p className="text-sm sm:text-base text-brand-muted mb-4 sm:mb-6 leading-relaxed">
            {error}
          </p>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to form
            </Button>
            <Button
              size="sm"
              onClick={onRetry}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-[hsl(var(--bg))] px-3 sm:px-4 py-6 sm:py-0">
      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,hsla(var(--brand-primary)/0.14),transparent_65%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_bottom,hsla(var(--brand-primary)/0.08),transparent_60%)]" />

      <div className="relative z-10 w-full max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-6 sm:mb-10 space-y-3 sm:space-y-5 text-center py-2 sm:py-4"
        >
          <motion.div
            className="mx-auto flex h-16 w-16 sm:h-24 sm:w-24 items-center justify-center rounded-2xl sm:rounded-3xl border-2 border-[hsl(var(--brand-primary)/0.2)] bg-linear-to-br from-[hsl(var(--brand-primary)/0.1)] to-[hsl(var(--brand-primary)/0.05)] shadow-lg backdrop-blur-sm"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="animate-pulse">
              <FileType2 className="h-8 w-8 sm:h-12 sm:w-12 text-brand-primary" />
            </div>
          </motion.div>
          <h2 className="text-2xl sm:text-4xl font-semibold text-[hsl(var(--fg))] font-heading tracking-tight">
            Generating your document
          </h2>
          <p className="text-sm sm:text-lg text-brand-muted leading-relaxed">
            Hang tight — this only takes a moment.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="rounded-2xl sm:rounded-3xl border border-brand-border bg-background p-4 sm:p-8 shadow-xl backdrop-blur-sm"
        >
          {/* Progress header */}
          <div className="mb-3 sm:mb-4 flex items-center justify-between gap-2 text-xs sm:text-sm font-medium">
            <span className="text-[hsl(var(--fg))]">{Math.round(progress)}% complete</span>
            <span className="text-brand-muted">
              Step {Math.min(stepIndex + 1, steps.length)} of {steps.length}
            </span>
          </div>

          {/* Progress bar */}
          <div className="relative h-4 overflow-hidden rounded-full border border-[hsl(var(--brand-primary)/0.25)] bg-[hsl(var(--brand-primary)/0.08)] shadow-inner">
            <motion.div
              className="absolute left-0 top-0 h-full rounded-full bg-linear-to-r from-brand-primary via-[hsl(var(--brand-primary)/0.9)] to-brand-primary shadow-lg"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            />
          </div>

          {/* Current stage card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={stepIndex}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="rounded-xl sm:rounded-2xl border border-brand-border bg-linear-to-br from-background to-[hsl(var(--brand-primary)/0.02)] p-4 sm:p-6 shadow-lg mt-4 sm:mt-6"
            >
              <div className="flex items-start gap-3 sm:gap-5">
                <div className="flex h-10 w-10 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-lg sm:rounded-xl border-2 border-[hsl(var(--brand-primary)/0.2)] bg-linear-to-br from-[hsl(var(--brand-primary)/0.1)] to-[hsl(var(--brand-primary)/0.05)]">
                  <div className="animate-pulse">
                    <Sparkles className="h-5 w-5 sm:h-7 sm:w-7 text-brand-primary" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="mb-1 sm:mb-2 text-base sm:text-xl font-semibold text-[hsl(var(--fg))] font-heading tracking-tight">
                    {currentStep.title}
                  </h3>
                  <p className="leading-relaxed text-brand-muted text-sm sm:text-base">
                    {currentStep.description}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Stage checklist */}
          <div className="mt-5 sm:mt-7 space-y-2 sm:space-y-3">
            {steps.map((step, index) => {
              const isCompleted = index < stepIndex;
              const isCurrent = index === stepIndex;
              return (
                <div
                  key={step.key}
                  className={`flex items-center gap-2.5 sm:gap-3 text-xs sm:text-sm transition-all duration-300 ${
                    isCompleted
                      ? 'text-brand-primary'
                      : isCurrent
                        ? 'text-[hsl(var(--fg))] font-medium'
                        : 'text-muted-foreground'
                  }`}
                >
                  {isCompleted ? (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
                      <Check className="h-3 w-3" />
                    </div>
                  ) : isCurrent ? (
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.8, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      className="h-5 w-5 rounded-full border-2 border-brand-primary bg-[hsl(var(--brand-primary)/0.1)]"
                    />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-brand-border bg-[hsl(var(--brand-border)/0.3)]" />
                  )}
                  <span>{step.title}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
